import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { getAuth } from 'firebase/auth';

export type OrgRole = 'ORG_OWNER' | 'ORG_ADMIN' | 'ORG_USER';

export type OrgUser = {
  /** null for a pending invite — nobody has accepted it, so there's no account yet. */
  firebaseUid: string | null;
  email: string;
  name: string | null;
  status: string | null;
};

/**
 * A row of /organization/members. Accepted members and pending invites share
 * this shape; only invites carry `invite`, and only they have a null
 * `user.firebaseUid`.
 */
export type OrgMember = {
  id: string;
  role: OrgRole;
  createdAt: string;
  user: OrgUser | null;
  invite?: {
    id: string;
    email: string;
    status: string;
    expiresAt: string;
  };
};

export function useListOrgMembers(enabled = true) {
  return useQuery({
    queryKey: ['orgMembers'],
    queryFn: async () => {
      const { data } = await api.get<{ members: OrgMember[] }>('/organization/members');
      return data.members;
    },
    enabled,
    staleTime: 10_000,
  });
}

export function useInviteOrgMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { email: string; role: 'ORG_ADMIN' | 'ORG_USER' }) => {
      const { data } = await api.post('/organization/members/invite', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgMembers'] });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useUpdateOrgMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { uid: string; role: 'ORG_ADMIN' | 'ORG_USER' }) => {
      const { data } = await api.patch(`/organization/members/${payload.uid}/role`, {
        role: payload.role,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgMembers'] });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useRemoveOrgMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (uid: string) => {
      const { data } = await api.delete(`/organization/members/${uid}`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgMembers'] });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

async function authedHeaders() {
  const u = getAuth().currentUser;
  if (!u) throw new Error('NOT_AUTHED');
  const token = await u.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

// GET /api/organization/invites/:token
export function useOrgInviteInfo(token: string, enabled: boolean) {
  return useQuery({
    queryKey: ['orgInvite', token],
    enabled: !!token && enabled,
    queryFn: async () => {
      const { data } = await api.get(`/organization/members/invites/${token}`);
      return data; // { orgName, email, role }
    },
  });
}

// POST /api/organization/invites/:token/accept
export function useAcceptOrgInvite(token: string) {
  return useMutation({
    mutationFn: async () => {
      const headers = await authedHeaders();
      const { data } = await api.post(
        `/organization/members/invites/${token}/accept`,
        {},
        { headers }
      );
      return data;
    },
  });
}
